import React from 'react'
import * as config from 'config'
import Button from 'uiComponents/buttons'
import { Modal, ModalBody, ModalFooter } from 'uiComponents/popups/modal.new'
import { LucideIcon } from 'utils/lucideIcon'
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard'
import './connectApp.scss'

interface ConnectAppModalProps {
    onDismiss: () => void
}

const CodeBlock: React.FC<{ code: string }> = ({ code }) => {
    const { copied, copy } = useCopyToClipboard()

    return (
        <div className="connect-app-code">
            <pre>{code}</pre>
            <button type="button" className="connect-app-copy" onClick={() => copy(code)}>
                <LucideIcon iconName={copied ? 'Check' : 'Copy'} size={14} />
                {copied ? 'Copied' : 'Copy'}
            </button>
        </div>
    )
}

const trimSlash = (url: string) => url.replace(/\/+$/, '')

export const ConnectAppModal: React.FC<ConnectAppModalProps> = ({ onDismiss }) => {
    const mcpEndpoint = config.get('mcp-endpoint')
    const identityEndpoint = config.get('identity-endpoint')

    const mcpUrl = mcpEndpoint ? `${trimSlash(mcpEndpoint)}/mcp` : null
    const tokenUrl = identityEndpoint ? `${trimSlash(identityEndpoint)}/oauth/token/` : null

    const directCall =
        mcpUrl &&
        [
            'curl -u "$CLIENT_ID:$CLIENT_SECRET" \\',
            "  -H 'Content-Type: application/json' \\",
            "  -H 'Accept: application/json, text/event-stream' \\",
            '  -d \'{"jsonrpc":"2.0","id":1,"method":"tools/list"}\' \\',
            `  ${mcpUrl}`,
        ].join('\n')

    const mintToken =
        tokenUrl &&
        [
            `JWT=$(curl -s -X POST ${tokenUrl} \\`,
            '  -d "grant_type=client_credentials" \\',
            '  -d "client_id=$CLIENT_ID" \\',
            '  -d "client_secret=$CLIENT_SECRET" \\',
            '  | jq -r .access_token)',
        ].join('\n')

    const bearerCall =
        mcpUrl &&
        [
            'curl -H "Authorization: Bearer $JWT" \\',
            "  -H 'Content-Type: application/json' \\",
            "  -H 'Accept: application/json, text/event-stream' \\",
            '  -d \'{"jsonrpc":"2.0","id":1,"method":"tools/list"}\' \\',
            `  ${mcpUrl}`,
        ].join('\n')

    return (
        <Modal title="Connect your app" onDismiss={onDismiss} className="connect-app-modal">
            <ModalBody>
                <p className="connect-app-intro">
                    A service app signs in with its own credentials — no browser, no human.
                </p>

                {mcpUrl && directCall && (
                    <section className="connect-app-step">
                        <h5>
                            <span className="connect-app-step-number">1</span>
                            Send the credentials directly
                        </h5>
                        <CodeBlock code={directCall} />
                        <p>
                            Point any MCP client at <code>{mcpUrl}</code> with a <code>Basic</code> authorization
                            header (<code>base64(client_id:client_secret)</code>) and you are done — nothing in your
                            config ever expires.
                        </p>
                    </section>
                )}

                {tokenUrl && mintToken && (
                    <section className="connect-app-step">
                        <h5>
                            <span className="connect-app-step-number">2</span>
                            Or mint the token yourself
                        </h5>
                        <CodeBlock code={mintToken} />
                        <p>
                            A token lasts as long as the sign-in length you picked for the app. Mint a fresh one per
                            run rather than storing it, then send it as a <code>Bearer</code> header on every request:
                        </p>
                        {bearerCall && <CodeBlock code={bearerCall} />}
                    </section>
                )}

                <p className="connect-app-footnote">
                    Keep the secret in a secrets manager or environment variable. If it leaks, rotate it here — the old
                    secret stops working immediately.
                </p>
            </ModalBody>
            <ModalFooter>
                <Button kind="action" onClick={onDismiss}>
                    Got it
                </Button>
            </ModalFooter>
        </Modal>
    )
}
